// The admin Coupons page: the offer types a coupon can have, checks before
// saving (the server checks the same things and has the last word — see
// backend/coupons.py), and how each coupon is described in the list.

export const COUPON_TYPES = [
  { value: 'percent_discount', label: 'Percentage Off' },
  { value: 'flat_discount', label: 'Flat Discount' },
  { value: 'free_delivery', label: 'Free Delivery' },
];

// Offered once but never worked: accepted at checkout, took nothing off.
// Existing ones stay in the admin list so they can be changed or deleted;
// customers never see them, and they can't be switched on.
const RETIRED_TYPES = { buy_x_get_y: 'Buy X Get Y', cashback: 'Cashback' };

export const isSupportedCouponType = (type) => COUPON_TYPES.some((t) => t.value === type);

export const couponTypeLabel = (type) =>
  COUPON_TYPES.find((t) => t.value === type)?.label || RETIRED_TYPES[type] || type;

const isDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
  && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

const isCount = (value) => value !== '' && value !== null && value !== undefined
  && Number.isInteger(Number(value)) && Number(value) >= 0;

// Field → message for everything wrong with the form; empty when it can be saved.
export const validateCouponForm = (form) => {
  const errors = {};
  const code = String(form.code || '').trim();
  if (!code) errors.code = 'Enter a code';
  else if (/\s/.test(code)) errors.code = "A coupon code can't contain spaces";
  if (!String(form.title || '').trim()) errors.title = 'Enter a title';

  if (!isSupportedCouponType(form.offer_type)) {
    if (form.is_active) errors.offer_type = 'Choose a supported offer type, or switch this coupon off';
  } else if (form.offer_type !== 'free_delivery') {
    const value = Number(form.discount_value);
    if (form.discount_value === '' || form.discount_value === null || !Number.isFinite(value) || value <= 0) {
      errors.discount_value = 'Enter a discount greater than 0';
    } else if (form.offer_type === 'percent_discount' && value > 100) {
      errors.discount_value = "A percentage discount can't be more than 100%";
    }
  }
  if (form.offer_type === 'percent_discount' && form.max_discount !== '' && form.max_discount !== null && form.max_discount !== undefined) {
    const cap = Number(form.max_discount);
    if (!Number.isFinite(cap) || cap <= 0) errors.max_discount = 'Leave empty for no cap, or enter more than 0';
  }
  const minOrder = Number(form.min_order_value || 0);
  if (!Number.isFinite(minOrder) || minOrder < 0) errors.min_order_value = 'Enter 0 or more';
  if (!isCount(form.max_uses_per_customer)) errors.max_uses_per_customer = 'Enter a whole number (0 = no limit)';
  if (!isCount(form.max_uses_total)) errors.max_uses_total = 'Enter a whole number (0 = no limit)';

  if (!isDate(form.start_date)) errors.start_date = 'Choose a start date';
  if (!isDate(form.end_date)) errors.end_date = 'Choose an end date';
  else if (isDate(form.start_date) && form.end_date < form.start_date) errors.end_date = 'The end date must be on or after the start date';
  return errors;
};

// The form's fields for an existing coupon. 0 means "no limit", so it is kept
// as 0 rather than replaced by a default.
export const couponToForm = (coupon) => ({
  code: coupon.code || '',
  title: coupon.title || '',
  description: coupon.description || '',
  offer_type: coupon.offer_type,
  discount_value: coupon.discount_value ?? 0,
  max_discount: coupon.max_discount || '',
  min_order_value: coupon.min_order_value || 0,
  buy_quantity: coupon.buy_quantity || 0,
  get_quantity: coupon.get_quantity || 0,
  applicable_product_ids: coupon.applicable_product_ids || [],
  applicable_category_ids: coupon.applicable_category_ids || [],
  is_new_customer_only: Boolean(coupon.is_new_customer_only),
  max_uses_total: coupon.max_uses_total ?? 0,
  max_uses_per_customer: coupon.max_uses_per_customer ?? 1,
  start_date: String(coupon.start_date || '').slice(0, 10),
  end_date: String(coupon.end_date || '').slice(0, 10),
  is_active: Boolean(coupon.is_active),
});

// What the form saves.
export const couponPayload = (form) => ({
  ...form,
  code: String(form.code || '').trim().toUpperCase(),
  title: String(form.title || '').trim(),
  discount_value: form.offer_type === 'free_delivery' ? 0 : Number(form.discount_value),
  max_discount: form.offer_type === 'percent_discount' && form.max_discount !== '' && form.max_discount !== null
    ? Number(form.max_discount) : null,
  min_order_value: Number(form.min_order_value || 0),
  buy_quantity: Number(form.buy_quantity || 0),
  get_quantity: Number(form.get_quantity || 0),
  max_uses_total: Number(form.max_uses_total),
  max_uses_per_customer: Number(form.max_uses_per_customer),
});

// 'active' | 'scheduled' | 'expired' | 'off' | 'retired', on the store's day
// (`today` as YYYY-MM-DD, from storeToday()) — dates are inclusive.
export const couponStatus = (coupon, today) => {
  if (!isSupportedCouponType(coupon.offer_type)) return 'retired';
  if (!coupon.is_active) return 'off';
  if (today < String(coupon.start_date || '').slice(0, 10)) return 'scheduled';
  if (today > String(coupon.end_date || '').slice(0, 10)) return 'expired';
  return 'active';
};

// "Used 3 of 100", "Used 3 times", or "Not used yet".
export const couponUsageText = (coupon) => {
  const used = Math.max(0, Number(coupon.total_used) || 0);
  const limit = Number(coupon.max_uses_total) || 0;
  if (limit > 0) return `Used ${used} of ${limit}`;
  if (used === 0) return 'Not used yet';
  return `Used ${used} ${used === 1 ? 'time' : 'times'}`;
};

// Whether the store offers coupons: its Coupons switch is on, or it is a store
// from before per-store switches (none set) — the admin menu's rule, and the
// server's (backend/coupons.py coupons_enabled).
export const storeHasCoupons = (storeSettings) => {
  const features = storeSettings?.tenant_features;
  return !features || Object.keys(features).length === 0 || features.coupons === true;
};

// The coupons a customer may be shown. The server already leaves out the
// retired types; this keeps an older server's list from showing them.
export const customerVisibleCoupons = (coupons) => (coupons || []).filter((c) => isSupportedCouponType(c.offer_type));
